import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faChevronRight,
  faCircleNotch,
  faClock,
  faSparkles,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "@/components/ui/dialog/Dialog";
import { Button } from "@/components/ui/button/Button";
import FormInput from "@/components/ui/form-input";
import { Stepper, type StepperStep } from "@/components/ui/stepper";
import { ClientAvatar } from "@/components/clients/ClientAvatar";
import * as AgenticV2API from "@/services/api/agentic-v2";
import type {
  FaClientSummary,
  MissionMatchCandidate,
  TaskCatalogueItem,
} from "@/services/api/agentic-v2";

/**
 * Task Command Palette.
 *
 * Step-based flow:
 *   1. Task    — pick a catalogue entry.
 *   2. Client  — pick the entity the task should act on.
 *   3. Mission — confirm linkage. We suggest an active mission whose
 *                template covers this task, or run standalone when no
 *                active mission matches. The user can always override.
 *
 * Submitting POSTs /tasks which queues the task and publishes it to the
 * worker. The footer always shows "what is about to happen" so the
 * launch is never a surprise.
 */
export interface TaskCommandPaletteProps {
  open: boolean;
  onClose: () => void;
  onLaunched?: (taskId: string, missionId: string | null) => void;
  /** Pre-select a client (e.g. when opened from a client detail page). */
  initialFaClientId?: string;
  /** Pre-select a catalogue entry (e.g. global search). */
  initialCatalogueId?: string;
  /** Seeded into POST /tasks ``inputs_resolved`` when the user launches. */
  initialInputsResolved?: Record<string, unknown>;
  /** Force-attach the new task to a specific mission and skip the Mission step. */
  lockMissionId?: string;
}

type Step = "task" | "client" | "mission";

const STEP_ORDER: Step[] = ["task", "client", "mission"];

const STEP_LABELS: Record<Step, string> = {
  task: "Task",
  client: "Client",
  mission: "Mission",
};

/** Shared with list-page search fields (Mission Control, Clients, Missions). */
const PALETTE_SEARCH_INPUT_CLASS =
  "rounded-md border border-slate-200 bg-white py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-200 dark:placeholder:text-slate-500 !h-auto min-h-10";

const PALETTE_LIST_SHELL_CLASS =
  "max-h-[340px] overflow-y-auto rounded-lg border border-slate-200 bg-slate-50/90 divide-y divide-slate-200 dark:border-white/5 dark:bg-slate-900/30 dark:divide-white/5";

const PALETTE_ROW_BASE_CLASS =
  "group flex w-full cursor-pointer items-start gap-3 px-3.5 py-3 text-left transition-colors";

function paletteRowClass(selected: boolean, align: "start" | "center" = "start"): string {
  const alignClass = align === "center" ? "items-center" : "items-start";
  return clsx(
    PALETTE_ROW_BASE_CLASS,
    alignClass,
    selected ? "bg-indigo-50 dark:bg-indigo-500/10" : "hover:bg-slate-100 dark:hover:bg-white/5",
  );
}

/**
 * Decide which step the palette should land on when opened.
 *
 * Rule: skip any step whose value is already known. Always land on the
 * first remaining step. If everything is known, land on Task so the user
 * can change their mind before launching.
 */
function pickInitialStep(opts: {
  catalogueId?: string;
  clientId?: string;
  lockMissionId?: string;
}): Step {
  if (!opts.catalogueId) return "task";
  if (!opts.clientId) return "client";
  if (opts.lockMissionId) return "task";
  return "mission";
}

export function TaskCommandPalette({
  open,
  onClose,
  onLaunched,
  initialFaClientId,
  initialCatalogueId,
  initialInputsResolved,
  lockMissionId,
}: TaskCommandPaletteProps) {
  const initialStep: Step = pickInitialStep({
    catalogueId: initialCatalogueId,
    clientId: initialFaClientId,
    lockMissionId,
  });
  const [step, setStep] = useState<Step>(initialStep);

  const [taskQuery, setTaskQuery] = useState("");
  const [catalogue, setCatalogue] = useState<TaskCatalogueItem[]>([]);
  const [loadingCatalogue, setLoadingCatalogue] = useState(false);
  const [selectedCatalogueId, setSelectedCatalogueId] = useState<string | null>(
    initialCatalogueId ?? null,
  );

  const [clients, setClients] = useState<FaClientSummary[]>([]);
  const [clientQuery, setClientQuery] = useState("");
  const [selectedClientId, setSelectedClientId] = useState<string | null>(
    initialFaClientId ?? null,
  );

  const [matches, setMatches] = useState<MissionMatchCandidate[]>([]);
  const [linkToMission, setLinkToMission] = useState(true);
  const [selectedMissionId, setSelectedMissionId] = useState<string | null>(
    null,
  );

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [launchInputs, setLaunchInputs] = useState<
    Record<string, unknown> | undefined
  >();

  const taskInputRef = useRef<HTMLInputElement>(null);
  const clientInputRef = useRef<HTMLInputElement>(null);

  // Reset state on open.
  useEffect(() => {
    if (!open) return;
    setTaskQuery("");
    setClientQuery("");
    setError(null);
    setSelectedCatalogueId(initialCatalogueId ?? null);
    setSelectedClientId(initialFaClientId ?? null);
    setMatches([]);
    setSelectedMissionId(lockMissionId ?? null);
    setLinkToMission(true);
    setLaunchInputs(initialInputsResolved);
    setStep(
      pickInitialStep({
        catalogueId: initialCatalogueId,
        clientId: initialFaClientId,
        lockMissionId,
      }),
    );
    setTimeout(() => {
      if (!initialCatalogueId) taskInputRef.current?.focus();
      else if (!initialFaClientId) clientInputRef.current?.focus();
    }, 50);
  }, [
    open,
    initialFaClientId,
    initialCatalogueId,
    initialInputsResolved,
    lockMissionId,
  ]);

  useEffect(() => {
    if (!initialCatalogueId || selectedCatalogueId === initialCatalogueId) {
      return;
    }
    setLaunchInputs(undefined);
  }, [selectedCatalogueId, initialCatalogueId]);

  // Re-focus the search input when entering a search-driven step.
  useEffect(() => {
    if (!open) return;
    setTimeout(() => {
      if (step === "task") taskInputRef.current?.focus();
      else if (step === "client") clientInputRef.current?.focus();
    }, 30);
  }, [step, open]);

  // Load catalogue when search changes.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingCatalogue(true);
    AgenticV2API.listTaskCatalogue({ q: taskQuery || undefined, limit: 30 })
      .then((rows) => {
        if (cancelled) return;
        setCatalogue(rows);
      })
      .catch(() => !cancelled && setCatalogue([]))
      .finally(() => !cancelled && setLoadingCatalogue(false));
    return () => {
      cancelled = true;
    };
  }, [open, taskQuery]);

  // Load clients — server-side search so the picker scales past the
  // picker endpoint's default page size. Debounced to avoid a request
  // per keystroke.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      AgenticV2API.listFaClients(clientQuery || undefined, 200, "all")
        .then((rows) => {
          if (!cancelled) setClients(rows);
        })
        .catch(() => {
          if (!cancelled) setClients([]);
        });
    }, clientQuery ? 200 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, clientQuery]);

  // No client-side filter — the server already filtered by ``q``.
  const filteredClients = clients;

  // Auto-match when both selections present.
  useEffect(() => {
    if (!open || !selectedCatalogueId || !selectedClientId) {
      setMatches([]);
      setSelectedMissionId(null);
      return;
    }
    let cancelled = false;
    AgenticV2API.matchCatalogueEntry(selectedCatalogueId, selectedClientId)
      .then((rows) => {
        if (cancelled) return;
        setMatches(rows);
        setLinkToMission(rows.length > 0);
        setSelectedMissionId(rows[0]?._id ?? null);
      })
      .catch(() => !cancelled && setMatches([]));
    return () => {
      cancelled = true;
    };
  }, [open, selectedCatalogueId, selectedClientId]);

  const selectedCatalogue = useMemo(
    () => catalogue.find((c) => c._id === selectedCatalogueId) ?? null,
    [catalogue, selectedCatalogueId],
  );
  const selectedClient = useMemo(
    () => clients.find((c) => c._id === selectedClientId) ?? null,
    [clients, selectedClientId],
  );

  const delegateSteps: StepperStep[] = useMemo(
    () =>
      STEP_ORDER.filter((s) => {
        if (s === "client" && initialFaClientId) return false;
        if (s === "mission" && lockMissionId) return false;
        return true;
      }).map((id) => ({ id, label: STEP_LABELS[id] })),
    [initialFaClientId, lockMissionId],
  );

  const canSubmit =
    !!selectedCatalogueId && !!selectedClientId && !submitting;

  const pickTask = (id: string) => {
    setSelectedCatalogueId(id);
    // Auto-advance to whichever step the user still needs to fill.
    if (!initialFaClientId) setStep("client");
    else if (!lockMissionId) setStep("mission");
    // else: nothing left — stay on task; the Launch button is now enabled.
  };

  const pickClient = (id: string) => {
    setSelectedClientId(id);
    if (!lockMissionId) setStep("mission");
  };

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || !selectedCatalogueId || !selectedClientId) return;
    setSubmitting(true);
    setError(null);
    try {
      const missionId = lockMissionId
        ? lockMissionId
        : linkToMission
          ? selectedMissionId
          : null;
      const result = await AgenticV2API.createTask({
        catalogue_id: selectedCatalogueId,
        entity_type: "fa_client",
        entity_id: selectedClientId,
        mission_id: missionId,
        ...(launchInputs ? { inputs_resolved: launchInputs } : {}),
      });
      onLaunched?.(result.task_id, result.mission_id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to launch task");
    } finally {
      setSubmitting(false);
    }
  }, [
    canSubmit,
    selectedCatalogueId,
    selectedClientId,
    linkToMission,
    selectedMissionId,
    lockMissionId,
    launchInputs,
    onLaunched,
    onClose,
  ]);

  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <DialogTitle>
        <div className="flex items-center justify-between gap-4">
          <span className="whitespace-nowrap">Delegate task</span>
          <Stepper
            steps={delegateSteps}
            activeStepId={step}
            isStepEnabled={(st) => {
              if (st.id === "task") return true;
              if (st.id === "client") return !!selectedCatalogueId;
              if (st.id === "mission")
                return !!selectedCatalogueId && !!selectedClientId;
              return false;
            }}
            onStepClick={(id) => setStep(id as Step)}
            data-testid="task-palette-stepper"
          />
        </div>
      </DialogTitle>

      <DialogBody className="!pt-4">
        <div className="min-h-[380px]">
          {step === "task" && (
            <TaskStep
              inputRef={taskInputRef}
              query={taskQuery}
              onQueryChange={setTaskQuery}
              loading={loadingCatalogue}
              rows={catalogue}
              selectedId={selectedCatalogueId}
              onPick={pickTask}
            />
          )}
          {step === "client" && (
            <ClientStep
              inputRef={clientInputRef}
              query={clientQuery}
              onQueryChange={setClientQuery}
              clients={filteredClients}
              selectedId={selectedClientId}
              onPick={pickClient}
            />
          )}
          {step === "mission" && (
            <MissionStep
              taskName={selectedCatalogue?.display_name ?? "Task"}
              clientName={selectedClient?.display_name ?? "client"}
              matches={matches}
              linkToMission={linkToMission}
              setLinkToMission={setLinkToMission}
              selectedMissionId={selectedMissionId}
              setSelectedMissionId={setSelectedMissionId}
            />
          )}
        </div>

        {error && (
          <p className="mt-3 text-sm text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        )}
      </DialogBody>

      <DialogActions>
        <Button plain onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          color="indigo"
          onClick={handleSubmit}
          disabled={!canSubmit}
          data-testid="task-palette-launch"
        >
          {submitting ? (
            <>
              <FontAwesomeIcon
                icon={faCircleNotch}
                className="animate-spin mr-2"
              />
              <span className="whitespace-nowrap">Launching...</span>
            </>
          ) : (
            <span className="whitespace-nowrap">Launch task</span>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Step 1: Task ───────────────────────────────────────────────────────

function TaskStep({
  inputRef,
  query,
  onQueryChange,
  loading,
  rows,
  selectedId,
  onPick,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>;
  query: string;
  onQueryChange: (q: string) => void;
  loading: boolean;
  rows: TaskCatalogueItem[];
  selectedId: string | null;
  onPick: (id: string) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <FormInput
        ref={inputRef}
        type="search"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        placeholder="Search task types..."
        icon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
        iconPosition="start"
        className={PALETTE_SEARCH_INPUT_CLASS}
        autoFocus
      />
      <div className={PALETTE_LIST_SHELL_CLASS}>
        {loading && rows.length === 0 && (
          <EmptyRow icon={faCircleNotch} spinning>
            Loading task types...
          </EmptyRow>
        )}
        {!loading && rows.length === 0 && (
          <EmptyRow icon={faMagnifyingGlass}>No task types match.</EmptyRow>
        )}
        {rows.map((row) => (
          <button
            key={row._id}
            type="button"
            onClick={() => onPick(row._id)}
            className={paletteRowClass(row._id === selectedId)}
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-indigo-100 to-violet-100 text-indigo-600 dark:from-indigo-500/30 dark:to-violet-500/30 dark:text-indigo-200">
              <FontAwesomeIcon icon={faSparkles} className="text-xs" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                {row.display_name}
              </div>
              <div className="mt-0.5 line-clamp-2 text-xs text-slate-600 dark:text-slate-400">
                {row.description}
              </div>
              <div className="mt-1.5 flex items-center gap-3 text-[11px] text-slate-500">
                {row.estimated_minutes !== null && (
                  <span className="inline-flex items-center gap-1">
                    <FontAwesomeIcon icon={faClock} className="text-[10px]" />
                    ~{row.estimated_minutes} min
                  </span>
                )}
                {row.tags.slice(0, 3).map((tag) => (
                  <span
                    key={tag}
                    className="rounded bg-slate-200/90 px-1.5 py-0.5 text-slate-600 dark:bg-slate-800/80 dark:text-slate-400"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <FontAwesomeIcon
              icon={faChevronRight}
              className="self-center text-xs text-slate-400 transition-colors group-hover:text-slate-600 dark:text-slate-600 dark:group-hover:text-slate-300"
            />
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Step 2: Client ─────────────────────────────────────────────────────

function ClientStep({
  inputRef,
  query,
  onQueryChange,
  clients,
  selectedId,
  onPick,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>;
  query: string;
  onQueryChange: (q: string) => void;
  clients: FaClientSummary[];
  selectedId: string | null;
  onPick: (id: string) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <FormInput
        ref={inputRef}
        type="search"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        placeholder="Search clients..."
        icon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
        iconPosition="start"
        className={PALETTE_SEARCH_INPUT_CLASS}
      />
      <div className={PALETTE_LIST_SHELL_CLASS}>
        {clients.length === 0 && (
          <EmptyRow icon={faMagnifyingGlass}>No clients match.</EmptyRow>
        )}
        {clients.map((c) => (
          <button
            key={c._id}
            type="button"
            onClick={() => onPick(c._id)}
            className={paletteRowClass(c._id === selectedId, "center")}
          >
            <ClientAvatar name={c.display_name} size="sm" />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-slate-900 dark:text-slate-100">{c.display_name}</div>
            </div>
            <FontAwesomeIcon
              icon={faChevronRight}
              className="text-xs text-slate-400 transition-colors group-hover:text-slate-600 dark:text-slate-600 dark:group-hover:text-slate-300"
            />
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Step 3: Mission ────────────────────────────────────────────────────

function MissionStep({
  taskName,
  clientName,
  matches,
  linkToMission,
  setLinkToMission,
  selectedMissionId,
  setSelectedMissionId,
}: {
  taskName: string;
  clientName: string;
  matches: MissionMatchCandidate[];
  linkToMission: boolean;
  setLinkToMission: (v: boolean) => void;
  selectedMissionId: string | null;
  setSelectedMissionId: (v: string | null) => void;
}) {
  const hasMatches = matches.length > 0;
  const missionCardClass = (active: boolean, disabled = false) =>
    clsx(
      "rounded-lg border px-4 py-3.5 text-left transition-colors",
      disabled &&
        "cursor-not-allowed border-slate-200 bg-slate-50 opacity-50 dark:border-white/5 dark:bg-slate-900/30",
      !disabled &&
        active &&
        "cursor-pointer border-indigo-300 bg-indigo-50 dark:border-indigo-400/40 dark:bg-indigo-500/10",
      !disabled &&
        !active &&
        "cursor-pointer border-slate-200 bg-white hover:bg-slate-50 dark:border-white/5 dark:bg-slate-900/30 dark:hover:bg-white/5",
    );

  return (
    <div className="flex flex-col gap-4">
      <div className="text-xs text-slate-600 dark:text-slate-400">
        Where should{" "}
        <span className="font-medium text-slate-900 dark:text-slate-200">{taskName}</span> for{" "}
        <span className="font-medium text-slate-900 dark:text-slate-200">{clientName}</span> run?
      </div>

      {/* Attach to mission */}
      <button
        type="button"
        disabled={!hasMatches}
        onClick={() => hasMatches && setLinkToMission(true)}
        className={missionCardClass(Boolean(hasMatches && linkToMission), !hasMatches)}
      >
        <div className="flex items-start gap-3">
          <RadioDot active={hasMatches && linkToMission} />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
              Attach to an active mission
            </div>
            {hasMatches ? (
              <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">
                We'll add this task to the selected mission so its results
                feed back into the plan.
              </p>
            ) : (
              <p className="mt-0.5 text-xs text-slate-500">
                No active mission covers this task for {clientName}.
              </p>
            )}
            {hasMatches && (
              <select
                value={selectedMissionId ?? ""}
                onChange={(e) => setSelectedMissionId(e.target.value || null)}
                onClick={(e) => e.stopPropagation()}
                className="mt-2.5 w-full cursor-pointer rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-900 focus:border-indigo-400 focus:outline-none dark:border-white/5 dark:bg-slate-900/60 dark:text-slate-200 dark:focus:border-indigo-400/40"
              >
                {matches.map((m) => (
                  <option key={m._id} value={m._id}>
                    {m.name}
                    {m.current_stage ? ` · ${m.current_stage}` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      </button>

      {/* Standalone */}
      <button
        type="button"
        onClick={() => setLinkToMission(false)}
        className={missionCardClass(!linkToMission || !hasMatches)}
      >
        <div className="flex items-start gap-3">
          <RadioDot active={!linkToMission || !hasMatches} />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
              Run standalone
            </div>
            <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">
              The task runs on its own and isn't linked to any mission.
            </p>
          </div>
        </div>
      </button>
    </div>
  );
}

function RadioDot({ active }: { active: boolean }) {
  return (
    <span
      className={clsx(
        "mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border",
        active ? "border-indigo-500 dark:border-indigo-300" : "border-slate-300 dark:border-slate-600",
      )}
    >
      {active && <span className="h-2 w-2 rounded-full bg-indigo-500 dark:bg-indigo-300" />}
    </span>
  );
}

// ── Shared bits ────────────────────────────────────────────────────────

function EmptyRow({
  icon,
  spinning,
  children,
}: {
  icon: typeof faMagnifyingGlass;
  spinning?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-center gap-2 px-3.5 py-6 text-xs text-slate-500 dark:text-slate-500">
      <FontAwesomeIcon
        icon={icon}
        className={spinning ? "animate-spin" : undefined}
      />
      {children}
    </div>
  );
}
